import { useMutation, useQueryClient } from '@tanstack/react-query';
import { sendMessage as sendMessageApi } from '../api/messageApi';
import { socketService } from '@/features/messaging/api/socketService';
import type { ConversationMessagesResponse, SendMessageDto } from '../types';
import { normalizeMessage } from '../utils/normalizeMessage';

/**
 * Hook for sending messages
 * 
 * Uses WebSocket for real-time delivery when connected,
 * falls back to REST API when WebSocket unavailable.
 * 
 * @param conversationId - Optional conversation ID to invalidate specific conversation cache
 */
export const useSendMessage = (conversationId?: string, senderId?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (dto: SendMessageDto) => {
      // Try WebSocket first if connected. The socket payload carries neither
      // `profileViewEngagementId`, the FA→SP `messageType`, nor the SP
      // `spMessageSubject` (feature 102), so a send that needs any of them
      // must go over REST (spec ticket 08 §5.1).
      if (
        socketService.isConnected()
        && !dto.profileViewEngagementId
        && !dto.messageType
        && !dto.spMessageSubject
      ) {
        return await socketService.sendMessage(dto.recipientId, dto.content);
      }

      // Fallback to REST API
      return await sendMessageApi(dto);
    },
    onMutate: async (dto) => {
      if (!conversationId) return;

      await queryClient.cancelQueries({ queryKey: ['conversation-messages', conversationId] });

      const previous = queryClient.getQueryData<ConversationMessagesResponse>([
        'conversation-messages',
        conversationId,
      ]);

      const optimisticId = `optimistic:${Date.now()}:${Math.random().toString(16).slice(2)}`;
      const optimisticMessage = normalizeMessage({
        id: optimisticId,
        conversationId,
        senderId: senderId ?? 'unknown-sender',
        recipientId: dto.recipientId,
        content: dto.content,
        status: 'SENT',
        createdAt: new Date(),
        readAt: null,
      } as any);

      queryClient.setQueryData<ConversationMessagesResponse>(
        ['conversation-messages', conversationId],
        (old) => {
          if (!old) {
            return {
              conversationId,
              participant: {
                id: dto.recipientId,
                name: 'Unknown',
                role: 'CARE_SEEKER',
                profilePhoto: null,
              },
              messages: [optimisticMessage],
              hasMore: false,
            };
          }

          const alreadyExists = old.messages.some((m) => m.id === optimisticId);
          if (alreadyExists) return old;

          return { ...old, messages: [...old.messages, optimisticMessage] };
        },
      );

      return { previous, optimisticId };
    },
    onError: (_error, _dto, context) => {
      if (!conversationId || !context) return;

      if (context.previous) {
        queryClient.setQueryData(['conversation-messages', conversationId], context.previous);
      } else {
        queryClient.removeQueries({ queryKey: ['conversation-messages', conversationId] });
      }
    },
    onSuccess: (message, _dto, context) => {
      const serverMessage = normalizeMessage(message as any);
      const serverConversationId = serverMessage.conversationId ?? conversationId;

      if (conversationId && context?.optimisticId) {
        queryClient.setQueryData<ConversationMessagesResponse>(
          ['conversation-messages', conversationId],
          (old) => {
            if (!old) return old;
            return {
              ...old,
              messages: old.messages.filter((m) => m.id !== context.optimisticId),
            };
          },
        );
      }

      if (serverConversationId) {
        queryClient.setQueryData<ConversationMessagesResponse>(
          ['conversation-messages', serverConversationId],
          (old) => {
            if (!old) {
              return {
                conversationId: serverConversationId,
                participant: {
                  id: serverMessage.recipientId,
                  name: 'Unknown',
                  role: 'CARE_SEEKER',
                  profilePhoto: null,
                },
                messages: [serverMessage],
                hasMore: false,
              };
            }

            const exists = old.messages.some((m) => m.id === serverMessage.id);
            if (exists) return old;

            return { ...old, messages: [...old.messages, serverMessage] };
          },
        );
      }
    },
    onSettled: (_data, _error, _dto) => {
      queryClient.invalidateQueries({ queryKey: ['conversations'] });

      // Targeted (not wildcard) invalidation of the active conversation's own
      // cache — converges it with the server even if the optimistic patch
      // above missed an edge case (spec ticket 09 §6.2). No `refetchType:
      // 'none'`: an active viewer of this conversation should refetch.
      if (conversationId) {
        queryClient.invalidateQueries({
          queryKey: ['conversation-messages', conversationId],
        });
      }
    },
  });
};

